import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, auth } from '../../config/firebase';
import { collection, query, where, getDocs, setDoc, doc, getDoc } from 'firebase/firestore';
import { getAuth,createUserWithEmailAndPassword } from 'firebase/auth';
import UserTable from '../components/UserTable';
import UserForm from '../components/UserForm';
import { useTenant } from '../../context/TenantContext';
import { useAuthState } from 'react-firebase-hooks/auth';
import '../../styles/UserManagement.css';
import { initializeApp } from 'firebase/app';
import { firebaseConfig } from '../../config/firebase';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { currentTenant } = useTenant();
  const [user] = useAuthState(auth);
  const [currentUserData, setCurrentUserData] = useState(null);

  const isSuperAdmin = currentUserData?.isSuperAdmin;
  const isAdmin = !isSuperAdmin && currentUserData?.isAdmin;

  useEffect(() => {
    const fetchCurrentUser = async () => {
      if (user) {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        setCurrentUserData(userDoc.data());
      }
    };
    fetchCurrentUser();
  }, [user]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        if (isSuperAdmin) {
          const tenantsSnapshot = await getDocs(collection(db, 'tenants'));
          setTenants(tenantsSnapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
        }

        let usersQuery;

        if (isSuperAdmin) {
          usersQuery = collection(db, 'users');
        } else if (isAdmin && currentTenant) {
          usersQuery = query(collection(db, 'users'), where('tenantId', '==', currentTenant.id));
        } else {
          usersQuery = query(collection(db, 'users'), where('uid', '==', user?.uid));
        }

        const usersSnapshot = await getDocs(usersQuery);
        setUsers(usersSnapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (error) {
        setError('Failed to load data: ' + error.message);
      } finally {
        setLoading(false);
      }
    };

    if (user && currentUserData) fetchData();
  }, [currentTenant, user, currentUserData,isAdmin,isSuperAdmin]);

  
  const handleCreateUser = async (userData) => {
    try {
      setError('');
      setLoading(true);
  
      const tenantId = isSuperAdmin
        ? userData.tenantId
        : currentTenant?.id;
  
      if (!tenantId) throw new Error('Tenant assignment is required');
  
      // Create a temporary firebase app (SECONDARY APP)
      const tempApp = initializeApp(firebaseConfig, 'tempApp');
      const tempAuth = getAuth(tempApp);
  
      // Create user without affecting current admin session
      const userCredential = await createUserWithEmailAndPassword(tempAuth, userData.email, userData.password);
  
      // Remove password before storing in Firestore
      const { password, ...userWithoutPassword } = userData;
  
      await setDoc(doc(db, 'users', userCredential.user.uid), {
        ...userWithoutPassword,
        uid: userCredential.user.uid,
        tenantId,
        isAdmin: isSuperAdmin ? userData.isAdmin : false,
        isSuperAdmin: false,
        isActive: true,
        createdAt: new Date(),
      });
  
      setShowForm(false);
  
      const usersQuery = isSuperAdmin
        ? collection(db, 'users')
        : query(collection(db, 'users'), where('tenantId', '==', tenantId));
  
      const usersSnapshot = await getDocs(usersQuery);
      setUsers(usersSnapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
  
      await tempAuth.signOut();
    } catch (error) {
      console.error('Error:', error);
      setError(error.code === 'permission-denied'
        ? 'You lack permissions for this action'
        : error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="user-management">
        <div className="page-header">
          <backbutton 
                onClick={() => navigate('/admin')} 
                className="backbutton"
              >
            &larr; Back
          </backbutton>
          <h1 className="header-center">User Management</h1>
          {(isSuperAdmin || isAdmin) && (
            <button 
              onClick={() => setShowForm(true)} 
              className="add-user-btn" 
              disabled={loading}
            >
              Connect New Tenant
            </button>
          )}
        </div>

      {error && <div className="error-message">{error}</div>}

      {showForm && (
        <div className="form-modal">
          <div className="form-content">
            <UserForm
              onSubmit={handleCreateUser}
              onCancel={() => setShowForm(false)}
              tenants={tenants}
              currentTenant={currentTenant}
              loading={loading}
              isSuperAdmin={isSuperAdmin}
              currentUser={currentUserData}
            />
          </div>
        </div>
      )}

      <UserTable users={users} />
    </div>
  );
};

export default UserManagement;
